// @ts-nocheck
import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Select,
  MenuItem,
  TextField,
  CircularProgress,
  alpha,
} from '@mui/material';
import { ReactComponent as CloseIcon } from '../../../../assets/Icons/lucideIcons/x.svg';
import isEqual from 'lodash/isEqual';
import { useTheme } from '@mui/material/styles';
import BasicDetails from '../BasicDetails/BasicDetails';
import { useDeleteDocumentMutation } from '../../../../features/document/api/documentInventoryQueries';
import useAttribute from '../../../../hooks/ModelInventory/useAttribute';
import useModelInventory from '../../../../hooks/ModelInventory/useModelInventory';
import useEntityDetailsForm from '../../../../hooks/useEntityDetailsForm';
import { isValueEmpty } from '../../../../utils/Utils';
import {
  applyDateCalendarConstraints,
  applyMandatoryConstraints,
} from '../../../../utils/formUtils';
import { EMPTY_ARRAY, EMPTY_OBJECT, NOOP } from '../../../../const/CommonConst';
import { ReactComponent as DropdownArrow } from '../../../../assets/images/DropdownArrow.svg';
import REGEX from '../../../../const/Regex';
import { DocumentCleanupProvider } from '../../../../context/DocumentCleanupContext';

/**
 * ConfigColumnRowDialog
 *
 * Dialog for adding / editing a single row in a config-column AdvancedTable.
 *
 * Flow:
 *   1. On open → fetch GET_ATTRIBUTE_SOURCE_MAPPING to learn which attribute IDs
 *      are "source" triggers for column constraints.
 *   2. Build a form using useEntityDetailsForm (same hook as EditModelDetails).
 *   3. Pass <BasicDetails> for rich field rendering (dropdowns, users, dates …).
 *   4. When a source attribute changes → POST get-column-constraints → apply
 *      autopopulate, lookup, editable, date_calander constraints.
 *   5. Save (default row):
 *      a. Validate mandatory visible fields via handleSaveFormPromise.
 *      b. POST calculate-row → merge response (computed columns).
 *      c. Call onSave({ rowType:'default', values:{A:..,B:..,C:..} }).
 *   6. Custom row (merged): save text directly, no API call.
 */
const ConfigColumnRowDialog = ({
  open,
  onClose,
  onSave,
  columns = EMPTY_ARRAY, // [{ colName:'A', attrId:931, attributeSchema:{...}, label:'...' }]
  entityData,
  advancedTableAttributeId,
  editRowValues = null, // { A: val, B: val, ... } or null
  rowNumber = null,
  rowType: initialRowType = 'default',
  editCustomText = '',
  usersList = EMPTY_ARRAY,
  teamUsersList = EMPTY_ARRAY,
  configColumn = false,
  isUserAuthorizedToOverrideAttribute = false,
}) => {
  const theme = useTheme();
  const {
    getAdvancedTableColumnConstraints,
    getAdvancedTableConstraintSources,
    calculateAdvancedTableRow,
    getOverrideAttributeValue,
  } = useAttribute();
  const { getAttributeDetails } = useModelInventory();
  const { mutateAsync: deleteDocument } = useDeleteDocumentMutation();

  // Track initial document IDs when dialog opens so newly uploaded documents
  // can be cleaned up from the server if the user cancels this dialog session.
  const initialDocIdsRef = useRef(new Set());
  useEffect(() => {
    if (open) {
      const ids = new Set();
      if (editRowValues) {
        columns.forEach((col) => {
          if (col.attributeSchema?.data_type === 'Document') {
            const val = editRowValues[col.colName] || editRowValues[col.attrId];
            const docs = Array.isArray(val) ? val : [val];
            docs.forEach((d) => {
              if (d?.document_id) ids.add(String(d.document_id));
            });
          }
        });
      }
      initialDocIdsRef.current = ids;
    }
  }, [open, editRowValues, columns]);

  // ── State ──────────────────────────────────────────────────────────────────
  const [rowType, setRowType] = useState(initialRowType);
  const [customText, setCustomText] = useState(editCustomText);
  const [isSaving, setIsSaving] = useState(false);
  // Override data is dialog-local — fetched here rather than threaded down as
  // a prop from the top-level entity form, since only this dialog ever reads it.
  const [entityOverrideData, setEntityOverrideData] = useState(EMPTY_ARRAY);

  const customTextError = useMemo(() => {
    if (rowType !== 'custom' || !customText) return '';
    if (!REGEX.ATTRIBUTE_NAME.test(customText)) {
      return 'Invalid section name. Only alphanumeric characters, spaces, hyphens, and underscores are allowed, starting with a letter.';
    }
    return '';
  }, [rowType, customText]);

  const fetchOverrideData = useCallback(() => {
    if (!entityData?.entity_id || !entityData?.entity_type) {
      return Promise.resolve();
    }
    return getOverrideAttributeValue(
      { entityType: entityData.entity_type, entityId: entityData.entity_id },
      false
    ).then((res) => {
      setEntityOverrideData(res?.data || EMPTY_ARRAY);
    });
  }, [
    entityData?.entity_id,
    entityData?.entity_type,
    getOverrideAttributeValue,
    setEntityOverrideData,
  ]);

  useEffect(() => {
    if (open) {
      fetchOverrideData();
    }
  }, [open]);

  // Overrides are stored per (entity, attribute, row_number, advanced_table_attribute_id).
  // Scope the entity-wide overrideData down to just this row so FieldInfo/
  // OverrideAttributeModal — which match purely by attributeId — never pick up
  // a standalone attribute's override or another row's override for the same column.
  const rowScopedOverrideData = useMemo(() => {
    if (!Array.isArray(entityOverrideData) || rowNumber == null) {
      return EMPTY_ARRAY;
    }
    return entityOverrideData.filter(
      (item) =>
        item?.row_number === rowNumber &&
        String(item?.advanced_table_attribute_id) ===
          String(advancedTableAttributeId)
    );
  }, [entityOverrideData, rowNumber, advancedTableAttributeId]);

  // Source attr tracking
  const sourceAttrIdsRef = useRef(new Set());
  const debounceTimerRef = useRef(null);
  const [sourceMapLoaded, setSourceMapLoaded] = useState(false);

  // Constraint state
  const [overrideDropdownOptions, setOverrideDropdownOptions] = useState({});
  const [editableConstraints, setEditableConstraints] = useState({});
  const [dateCalendarConstraints, setDateCalendarConstraints] = useState({});
  const [lookupSourceAttributes, setLookupSourceAttributes] = useState([]);
  const [autoCompleteSourceAttributes, setAutoCompleteSourceAttributes] =
    useState([]);
  const [dateCalendarSourceAttributes, setDateCalendarSourceAttributes] =
    useState([]);
  const [editableSourceAttributes, setEditableSourceAttributes] = useState([]);
  const [mandatoryConstraints, setMandatoryConstraints] = useState({});
  const [mandatorySourceAttributes, setMandatorySourceAttributes] = useState(
    []
  );
  const [isLoadingConstraints, setIsLoadingConstraints] = useState(false);

  const isEditMode = useMemo(
    () =>
      editRowValues !== null ||
      (initialRowType === 'custom' && editCustomText !== ''),
    [editRowValues, initialRowType, editCustomText]
  );

  const handleRowTypeChange = useCallback(
    (e) => {
      setRowType(e?.target?.value);
    },
    [setRowType]
  );

  const handleCustomTextChange = useCallback(
    (e) => {
      setCustomText(e?.target?.value);
    },
    [setCustomText]
  );

  // ── Build attributes list from column schemas ────────────────────────────
  const attributes = useMemo(
    () =>
      columns.map((col) => ({
        ...(col.attributeSchema || {}),
      })),
    [columns]
  );

  // ── formDetails shape expected by useEntityDetailsForm ───────────────────
  const formDetails = useMemo(
    () => ({ attributes, sections: [] }),
    [attributes]
  );

  const allSectionList = useMemo(
    () => [
      {
        section_name: 'Row Details',
        attributes: attributes,
      },
    ],
    [attributes]
  );

  // ── attributeDetails: attrId → value (from editRowValues or blank) ───────
  // For User/Group MULTI_SELECT, convert stored arrays of IDs → joined display names,
  // mirroring the same pre-processing BasicDetails does before passing to FieldInfo.
  const attributeDetails = useMemo(() => {
    const vals = {};
    columns.forEach((col) => {
      vals[col.attrId] = editRowValues?.[col.colName] ?? '';
    });
    return vals;
  }, [columns, editRowValues]);

  // ── useEntityDetailsForm — same hook used in EditModelDetails ────────────
  const {
    entityDetailsFormikForm,
    handleSaveFormPromise,
    updateInitialValuesFromAPI,
    setFormSchema,
  } = useEntityDetailsForm({
    formDetails,
    attributeDetails,
    entityId: entityData?.entity_id,
    entityType: entityData?.entity_type,
    // No actual API update — we collect values and call onSave ourselves
    updateEntityCallback: () => Promise.resolve(true),
    skipUnchangedValueCheck: true,
  });

  // ── Build current_row_values from formik (col-letter keyed) ─────────────
  const buildCurrentRowValues = useCallback(
    (formikValues) => {
      const result = {};
      columns.forEach((col) => {
        const val = formikValues[col.attrId];
        result[col.colName] = val === '' || val === undefined ? null : val;
      });
      return result;
    },
    [columns]
  );

  // ── Helper: validate constraint values against new options ──────────────
  const validateConstraintValues = useCallback(
    (newOverrideOptions) => {
      Object.keys(newOverrideOptions).forEach((attrId) => {
        const currentValue = entityDetailsFormikForm?.values?.[attrId];
        const newOptions = newOverrideOptions[attrId];

        if (
          !isValueEmpty(currentValue) &&
          newOptions &&
          Array.isArray(newOptions)
        ) {
          if (newOptions.length === 0) {
            entityDetailsFormikForm?.setFieldValue(attrId, null);
            return;
          }

          const validOptionValues = newOptions.map((option) =>
            typeof option === 'object'
              ? option?.value || option?.entity_id
              : option
          );

          if (Array.isArray(currentValue)) {
            const validValues = currentValue.filter((val) => {
              const valToCheck = val?.value || val?.entity_id || val;
              return validOptionValues.includes(valToCheck);
            });
            const correctValue = validValues.length > 0 ? validValues : null;
            if (!isEqual(currentValue, correctValue)) {
              entityDetailsFormikForm?.setFieldValue(attrId, correctValue);
            }
          } else {
            const valToCheck =
              currentValue?.value || currentValue?.entity_id || currentValue;
            const isCurrentValueValid = validOptionValues.includes(valToCheck);
            if (!isCurrentValueValid) {
              entityDetailsFormikForm?.setFieldValue(attrId, null);
            }
          }
        }
      });
    },
    [entityDetailsFormikForm]
  );

  // ── Helper: handle auto population ───────────────────────────────────────
  const handleAutoPopulation = useCallback(
    (autoPopulateData) => {
      Object.keys(autoPopulateData || {}).forEach((attrId) => {
        const autoPopulatedValue = autoPopulateData[attrId];
        const currentValue = entityDetailsFormikForm?.values?.[attrId];

        if (!isValueEmpty(autoPopulatedValue) || isValueEmpty(currentValue)) {
          const valueToSet = !isValueEmpty(autoPopulatedValue)
            ? autoPopulatedValue
            : currentValue;

          if (currentValue === valueToSet) return;

          entityDetailsFormikForm?.setFieldValue(attrId, valueToSet);
        }
      });
    },
    [entityDetailsFormikForm]
  );

  // ── Apply constraint API response ────────────────────────────────────────
  const applyConstraints = useCallback(
    (constraintResp) => {
      if (!constraintResp) return;
      const {
        lookup = EMPTY_OBJECT,
        date_calendar = EMPTY_OBJECT,
        editable = EMPTY_OBJECT,
        mandatory = EMPTY_OBJECT,
        auto_populate = EMPTY_OBJECT,
        lookup_source_attributes = EMPTY_ARRAY,
        autocomplete_source_attributes = EMPTY_ARRAY,
        date_calander_attributes = EMPTY_ARRAY,
        editable_source_attributes = EMPTY_ARRAY,
        mandatory_source_attributes = EMPTY_ARRAY,
      } = constraintResp;

      // Merge instead of overwrite to ensure all active constraints are preserved
      setOverrideDropdownOptions((prev) => ({ ...prev, ...lookup }));
      setDateCalendarConstraints((prev) => ({ ...prev, ...date_calendar }));
      setEditableConstraints((prev) => ({ ...prev, ...editable }));
      setMandatoryConstraints((prev) => ({ ...prev, ...mandatory }));

      setLookupSourceAttributes(lookup_source_attributes);
      setAutoCompleteSourceAttributes(autocomplete_source_attributes);
      setDateCalendarSourceAttributes(date_calander_attributes);
      setEditableSourceAttributes(editable_source_attributes);
      setMandatorySourceAttributes(mandatory_source_attributes);

      // Handle autopopulate
      if (auto_populate && Object.keys(auto_populate).length > 0) {
        handleAutoPopulation(auto_populate);
      }

      // Handle date schema constraints - always try to apply them even if empty to ensure cleared constraints are removed
      if (date_calendar !== undefined) {
        setFormSchema((prevSchema) => {
          if (!prevSchema) return prevSchema;
          return applyDateCalendarConstraints(prevSchema, date_calendar);
        });
      }

      // Handle mandatory schema constraints
      if (mandatory !== undefined) {
        setFormSchema((prevSchema) => {
          if (!prevSchema) return prevSchema;
          return applyMandatoryConstraints(prevSchema, mandatory, attributes);
        });
      }

      // Validate current values against new dropdown options
      validateConstraintValues(lookup);
    },
    [
      handleAutoPopulation,
      validateConstraintValues,
      setFormSchema,
      setOverrideDropdownOptions,
      setDateCalendarConstraints,
      setEditableConstraints,
      setMandatoryConstraints,
      setLookupSourceAttributes,
      setAutoCompleteSourceAttributes,
      setDateCalendarSourceAttributes,
      setEditableSourceAttributes,
      setMandatorySourceAttributes,
    ]
  );

  // ── Fetch and apply column constraints ──────────────────────────────────
  const fetchAndApplyConstraints = useCallback(
    async (currentRowVals) => {
      if (
        !entityData?.entity_id ||
        !entityData?.entity_type ||
        !advancedTableAttributeId
      ) {
        return;
      }

      setIsLoadingConstraints(true);
      try {
        const res = await getAdvancedTableColumnConstraints({
          attribute_id: advancedTableAttributeId,
          entity_id: entityData?.entity_id,
          entity_type: entityData?.entity_type,
          current_row_values: currentRowVals,
        });
        if (res?.data) {
          applyConstraints(res.data);
        }
      } finally {
        setIsLoadingConstraints(false);
      }
    },
    [
      entityData?.entity_id,
      entityData?.entity_type,
      advancedTableAttributeId,
      applyConstraints,
      setIsLoadingConstraints,
    ]
  );

  // ── Fetch source mapping on open ─────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    if (!entityData?.entity_id || !advancedTableAttributeId) {
      setSourceMapLoaded(true);
      return;
    }
    setSourceMapLoaded(false);
    getAdvancedTableConstraintSources({
      entity_id: entityData?.entity_id,
      entity_type: entityData?.entity_type,
      attribute_id: advancedTableAttributeId,
    })
      .then((res) => {
        if (res?.data) {
          const sources = [
            ...(res.data?.lookup_source_attributes || EMPTY_ARRAY),
            ...(res.data?.autocomplete_source_attributes || EMPTY_ARRAY),
            ...(res.data?.date_calander_attributes || EMPTY_ARRAY),
            ...(res.data?.editable_source_attributes || EMPTY_ARRAY),
            ...(res.data?.mandatory_source_attributes || EMPTY_ARRAY),
          ];
          setLookupSourceAttributes(
            res.data.lookup_source_attributes || EMPTY_ARRAY
          );
          setAutoCompleteSourceAttributes(
            res.data?.autocomplete_source_attributes || EMPTY_ARRAY
          );
          setDateCalendarSourceAttributes(
            res.data?.date_calander_attributes || EMPTY_ARRAY
          );
          setEditableSourceAttributes(
            res.data?.editable_source_attributes || EMPTY_ARRAY
          );
          setMandatorySourceAttributes(
            res.data?.mandatory_source_attributes || EMPTY_ARRAY
          );
          sourceAttrIdsRef.current = new Set(sources.map(String));
        }
      })
      .catch(() => {
        sourceAttrIdsRef.current = new Set();
      })
      .finally(() => {
        setSourceMapLoaded(true);
      });
  }, [
    open,
    entityData?.entity_id,
    entityData?.entity_type,
    advancedTableAttributeId,
  ]);

  // ── Fetch initial constraints when dialog opens ──────────────────────────
  useEffect(() => {
    if (!open || !sourceMapLoaded) return;
    if (!entityData?.entity_id || !advancedTableAttributeId) return;

    const initialRowValues = buildCurrentRowValues(
      columns.reduce(
        (acc, col) => ({
          ...acc,
          [col.attrId]: editRowValues?.[col.colName] ?? '',
        }),
        {}
      )
    );

    fetchAndApplyConstraints(initialRowValues);
  }, [
    open,
    sourceMapLoaded,
    entityData?.entity_id,
    entityData?.entity_type,
    advancedTableAttributeId,
    buildCurrentRowValues,
    columns,
    editRowValues,
  ]);

  // ── Handle attribute value change — trigger constraint if source ─────────
  const handleAttributeValueChange = useCallback(
    (attrId, newVal) => {
      entityDetailsFormikForm.setFieldValue(attrId, newVal);

      const attrIdStr = String(attrId);

      if (
        !sourceAttrIdsRef.current.has(attrIdStr) ||
        !entityData?.entity_id ||
        !entityData?.entity_type ||
        !advancedTableAttributeId
      ) {
        return;
      }

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(() => {
        const currentRowValues = buildCurrentRowValues({
          ...entityDetailsFormikForm.values,
          [attrId]: newVal,
        });

        fetchAndApplyConstraints(currentRowValues);
      }, 700);
    },
    [
      entityDetailsFormikForm,
      entityData?.entity_id,
      entityData?.entity_type,
      advancedTableAttributeId,
      buildCurrentRowValues,
      fetchAndApplyConstraints,
    ]
  );

  // ── Save handler ─────────────────────────────────────────────────────────
  const handleSave = useCallback(async () => {
    if (rowType === 'custom') {
      onSave({ rowType: 'custom', customText });
      return;
    }

    // Validate using the hook's Yup schema + visible attribute filtering
    try {
      await handleSaveFormPromise({ skipValidation: false });
    } catch {
      // Validation failed — hook already sets errors/touched, keep dialog open
      return;
    }

    setIsSaving(true);
    try {
      const currentRowValues = buildCurrentRowValues(
        entityDetailsFormikForm.values
      );

      const calcRes = await calculateAdvancedTableRow({
        attribute_id: advancedTableAttributeId,
        entity_id: entityData?.entity_id,
        entity_type: entityData.entity_type,
        values: currentRowValues,
        row_number: rowNumber ?? null,
      });

      const responseValues = calcRes?.data?.values || {};
      const mergedValues = { ...currentRowValues };
      Object.entries(responseValues).forEach(([colLetter, val]) => {
        if (val !== '' && val != null) {
          mergedValues[colLetter] = val;
        }
      });

      onSave({ rowType: 'default', values: mergedValues });
    } catch (_err) {
      // Save failed — keep dialog open
    } finally {
      setIsSaving(false);
    }
  }, [
    rowType,
    customText,
    handleSaveFormPromise,
    entityDetailsFormikForm,
    buildCurrentRowValues,
    calculateAdvancedTableRow,
    advancedTableAttributeId,
    entityData?.entity_id,
    entityData?.entity_type,
    rowNumber,
    onSave,
    setIsSaving,
  ]);

  const cleanupRef = useRef(null);

  const handleClose = useCallback(() => {
    // Clean up any documents uploaded during this dialog session
    if (cleanupRef.current?.cleanupAllDocuments) {
      cleanupRef.current.cleanupAllDocuments();
    }
    columns.forEach((col) => {
      if (col.attributeSchema?.data_type === 'Document') {
        const val = entityDetailsFormikForm?.values?.[col.attrId];
        const docs = Array.isArray(val) ? val : [val];
        docs.forEach((d) => {
          if (
            d?.document_id &&
            !initialDocIdsRef.current.has(String(d.document_id))
          ) {
            deleteDocument({ document_id: d.document_id });
          }
        });
      }
    });
    entityDetailsFormikForm.resetForm();
    setRowType('default');
    setCustomText('');
    setOverrideDropdownOptions({});
    setEditableConstraints({});
    setDateCalendarConstraints({});
    setMandatoryConstraints({});
    setLookupSourceAttributes([]);
    setAutoCompleteSourceAttributes([]);
    setDateCalendarSourceAttributes([]);
    setEditableSourceAttributes([]);
    setMandatorySourceAttributes([]);
    onClose();
  }, [entityDetailsFormikForm, onClose, columns, deleteDocument]);

  const isCustomMode = useMemo(() => rowType === 'custom', [rowType]);

  const handleRefreshSingleAttribute = useCallback(
    (attrId, newVal) => {
      entityDetailsFormikForm.setFieldValue(attrId, newVal);
    },
    [entityDetailsFormikForm]
  );

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <DocumentCleanupProvider cleanupRef={cleanupRef}>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="subtitle1">
              {isEditMode ? 'Edit Row' : 'Add Row'}
            </Typography>
            <IconButton size="small" onClick={handleClose}>
              <CloseIcon width={20} height={20} />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent
          dividers
          sx={[
            {
              minHeight: isCustomMode ? 0 : 160,
              overflowX: 'hidden',
              overflowY: 'auto',
              p: 0,
            },
          ]}
        >
          {/* Row Type + Custom Text strip */}
          <Box
            sx={[
              {
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                px: 3,
                py: 2,
                borderBottom: isCustomMode
                  ? 'none'
                  : `1px solid ${alpha(theme?.palette?.colorPicker?.black, 0.1)}`,
              },
            ]}
          >
            <Typography
              variant="body1"
              sx={{ whiteSpace: 'nowrap', lineHeight: '40px' }}
            >
              Row Type:
            </Typography>
            <Select
              size="small"
              value={rowType}
              onChange={handleRowTypeChange}
              sx={{ minWidth: 140, height: 40, m: 0 }}
              IconComponent={DropdownArrow}
            >
              <MenuItem value="default">Default</MenuItem>
              <MenuItem value="custom">Custom</MenuItem>
            </Select>

            {isCustomMode && (
              <Typography
                variant="body1"
                sx={{
                  whiteSpace: 'nowrap',
                  ml: 1,
                  lineHeight: '40px',
                }}
              >
                Section name:
              </Typography>
            )}
            {isCustomMode && (
              <TextField
                size="small"
                placeholder="Enter section name"
                value={customText}
                onChange={handleCustomTextChange}
                error={!!customTextError}
                helperText={customTextError}
                sx={{
                  flex: 1,
                  m: 0,
                  '& .MuiInputBase-root': { height: 40 },
                  minWidth: 50,
                }}
              />
            )}
          </Box>

          {/* Attribute form — hidden in custom mode */}
          {!isCustomMode && (
            <Box sx={{ p: 0 }}>
              {attributes?.length === 0 ? (
                <Typography
                  variant="body2"
                  sx={{ color: 'text.secondary', textAlign: 'center', py: 4 }}
                >
                  No columns configured.
                </Typography>
              ) : (
                <BasicDetails
                  attributes={attributes}
                  attributeValues={entityDetailsFormikForm.values}
                  entityDetailsFormikForm={entityDetailsFormikForm}
                  formValues={entityDetailsFormikForm.values}
                  setFormValues={NOOP}
                  mode="edit"
                  isMultipleEditMode={true}
                  lookupSourceAttributes={lookupSourceAttributes}
                  autoCompleteSourceAttributes={autoCompleteSourceAttributes}
                  dateCalendarSourceAttributes={dateCalendarSourceAttributes}
                  editableSourceAttributes={editableSourceAttributes}
                  mandatoryConstraints={mandatoryConstraints}
                  mandatorySourceAttributes={mandatorySourceAttributes}
                  overrideDropdownOptions={overrideDropdownOptions}
                  editableConstraints={editableConstraints}
                  dateCalendarConstraints={dateCalendarConstraints}
                  isLoadingConstraints={isLoadingConstraints}
                  handleAttributeValueChange={handleAttributeValueChange}
                  onEditAttribute={NOOP}
                  usersList={usersList}
                  teamUsersList={teamUsersList}
                  isUserAuthorizedForUpdateAttribute
                  allAttributeChats={EMPTY_ARRAY}
                  handleOpenChat={NOOP}
                  handleAttributeClick={NOOP}
                  entityData={entityData}
                  isUserAuthorizedToOverrideAttribute={
                    isUserAuthorizedToOverrideAttribute
                  }
                  overrideData={rowScopedOverrideData}
                  rowNumber={rowNumber}
                  advancedTableAttributeId={advancedTableAttributeId}
                  refreshSingleAttribute={handleRefreshSingleAttribute}
                  handleAutoSave={NOOP}
                  getOverrideData={fetchOverrideData}
                  getAttributeDetails={getAttributeDetails}
                  updateInitialValuesFromAPI={updateInitialValuesFromAPI}
                  allSectionList={allSectionList}
                  configColumn={configColumn}
                />
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleClose} variant="outlined" disabled={isSaving}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={
              isSaving ||
              (isCustomMode && (!customText.trim() || !!customTextError)) ||
              (!isCustomMode && !sourceMapLoaded)
            }
            startIcon={
              isSaving ? <CircularProgress size={16} color="inherit" /> : null
            }
          >
            {isEditMode ? 'Update Row' : 'Save Row'}
          </Button>
        </DialogActions>
      </Dialog>
    </DocumentCleanupProvider>
  );
};

ConfigColumnRowDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      colName: PropTypes.string,
      attrId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      attributeSchema: PropTypes.shape({}),
      label: PropTypes.string,
    })
  ),
  advancedTableAttributeId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  editRowValues: PropTypes.shape({}),
  rowNumber: PropTypes.number,
  rowType: PropTypes.oneOf(['default', 'custom']),
  editCustomText: PropTypes.string,
  usersList: PropTypes.arrayOf(PropTypes.shape({})),
  teamUsersList: PropTypes.arrayOf(PropTypes.shape({})),
  entityData: PropTypes.shape({
    entity_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    entity_type: PropTypes.string,
  }),
  configColumn: PropTypes.bool,
  isUserAuthorizedToOverrideAttribute: PropTypes.bool,
};

export default ConfigColumnRowDialog;
